import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { DraftPlayer } from '@/types/draftPlayer';

/**
 * Maps a database player row to the DraftPlayer interface.
 * Rank is assigned by index; projection/ADP fields get placeholders
 * until the scoring engine provides real projections.
 */
const mapDbPlayer = (row: any): DraftPlayer => {
  // Map MLB position types to draft-friendly abbreviations
  let position = row.position_abbreviation;

  // Use pitcher_role if available for pitchers
  if (position === 'P' && row.pitcher_role) {
    position = row.pitcher_role; // 'SP' or 'RP'
  } else if (position === 'P') {
    position = 'SP'; // fallback
  }
  // Map LF/CF/RF to generic OF
  if (['LF', 'CF', 'RF'].includes(position)) position = 'OF';
  // Map TWP (Two-Way Player) to UTIL
  if (position === 'TWP') position = 'UTIL';

  const adp = row.adp ?? 9999;

  return {
    id: row.id,
    name: row.full_name,
    position,
    team: row.mlb_team_abbreviation || 'FA',
    byeWeek: 0, // MLB doesn't have bye weeks
    projectedTrashPoints: Number(row.projected_trash_points) || 0,
    adp,
    rank: adp,
    tier: Math.ceil(adp / 50),
    headshot_url: row.headshot_url,
    mlb_team_name: row.mlb_team_name,
    position_eligibility: row.position_eligibility || undefined,
  };
};

export const useDraftPlayers = (sport: string = 'mlb') => {
  const [players, setPlayers] = useState<DraftPlayer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlayers = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Paginate to get all players beyond the 1000-row default limit
        let allData: any[] = [];
        let from = 0;
        const pageSize = 1000;
        while (true) {
          const { data: batch, error: fetchError } = await supabase
            .from('players')
            .select('*')
            .eq('sport', sport)
            .eq('status', 'Active')
            .order('adp', { ascending: true })
            .range(from, from + pageSize - 1);
          if (fetchError) throw fetchError;
          allData = allData.concat(batch || []);
          if (!batch || batch.length < pageSize) break;
          from += pageSize;
        }
        const data = allData;

        const mapped = (data || []).map((row: any) => mapDbPlayer(row));
        setPlayers(mapped);
      } catch (err: any) {
        console.error('Failed to fetch draft players:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlayers();
  }, [sport]);

  return { players, isLoading, error };
};
